import {
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  VStack,
  Text,
  Button,
  Divider,
  Box
} from '@chakra-ui/react';
import React, { FC, useEffect } from 'react';
import { NFTCartItem, useCartStore } from '@app/state/nftCartStore';
import { NftCartItem } from '@app/screens/nft-marketplace/Cart/NftCartItem';
import { SubmitSection } from '@app/screens/nft-marketplace/ApproveDialog/components/SubmitSection';
import { CartTotalSection } from '@app/screens/nft-marketplace/Cart/CartTotalSection';
import { calculateSubtotalsPerCurrency } from '@app/helpers/nft/nftCartHelpers';
import { useGetListingsForCart } from '@app/hooks/nft/useGetListingsForCart';
import { getTokensWhitelist } from '@app/constants/tokensWhitelist';
import { useConfig } from '@app/config';
import { ListLoader } from '@app/screens/pools/components/ListLoader';
import { ApplicationConfig } from '@app/config/types';
import { NftRewardCartItem } from '@app/screens/nft-marketplace/Cart/NftRewardCartItem';
import { useMedia } from 'react-use';

interface Props {}

export const Cart: FC<Props> = () => {
  const isMobile = useMedia('(max-width: 480px)', true);
  const { clearCart, totalItems, getListingIds } = useCartStore();
  const config = useConfig() as ApplicationConfig;
  const TOKENS_WHITELIST = getTokensWhitelist(config);
  const listingIdsFromStore = getListingIds();
  const { data, isLoading: isLoadingListings } =
    useGetListingsForCart(listingIdsFromStore);

  const cartSubtotals = data
    ? calculateSubtotalsPerCurrency(data, TOKENS_WHITELIST)
    : null;

  const renderItemsCount = () => {
    const count = totalItems();

    return `${count} ${count === 1 ? 'Item' : 'Items'}`;
  };

  const renderList = () =>
    data?.length ? (
      data.map(listing => {
        if (listing.nftPool) {
          return (
            <NftCartItem
              key={`${listing.id}-${listing.tokenId}`}
              isMobile={isMobile}
              listing={listing}
            />
          );
        } else {
          return (
            <NftRewardCartItem
              key={`${listing.id}-${listing.tokenId}`}
              isMobile={isMobile}
              listing={listing}
            />
          );
        }
      })
    ) : (
      <CartEmptyMessage />
    );

  const CartEmptyMessage = () => (
    <Box>
      <Text
        fontSize="24px"
        fontWeight="500"
        color="white"
        mb="16px"
        textAlign="center"
      >
        The cart is empty.
      </Text>
    </Box>
  );

  return (
    <VStack>
      <HStack width="100%" justifyContent="space-between" padding="8px 16px">
        <Text
          fontWeight="400"
          fontSize="16px"
          lineHeight="22px"
          color="gray.15"
        >
          {renderItemsCount()}
        </Text>
        <Button
          borderRadius="8px"
          border="1px"
          borderColor="gray.15"
          fontSize="14px"
          lineHeight="14px"
          color="gray.15"
          padding="8px 16px"
          onClick={clearCart}
        >
          Clear all
        </Button>
      </HStack>
      {isLoadingListings ? <ListLoader /> : renderList()}
      <Divider
        sx={{ margin: '28px 0', background: 'gray.15', height: '1px' }}
      />
      {cartSubtotals && data && (
        <>
          <CartTotalSection groups={cartSubtotals} />
          <SubmitSection
            cartSubtotals={cartSubtotals}
            listings={data}
            config={config}
          />
        </>
      )}
    </VStack>
  );
};
